// src/components/ProjectList.jsx
import React, { useEffect, useState } from "react";
import api from "../api";

export default function ProjectList() {
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState("Pending");
  const [ownerId, setOwnerId] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [editingId, setEditingId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  /* ================= LOAD DATA ================= */

  useEffect(() => {
    loadProjects();
    loadEmployees();
  }, []);

  const loadProjects = async () => {
    try {
      const res = await api.get("/project");
      setProjects(res.data);
    } catch (err) {
      console.error("PROJECT LOAD ERROR:", err);
      setError("Failed to load projects");
    }
  };

  const loadEmployees = async () => {
    try {
      const res = await api.get("/employees");
      setEmployees(res.data);
      if (res.data.length > 0) {
        setOwnerId(res.data[0].id);
      }
    } catch (err) {
      console.error("EMPLOYEE LOAD ERROR:", err);
      setError("Failed to load employees");
    }
  };

  /* ================= SUBMIT ================= */

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!name.trim() || !ownerId) {
      setError("Project name and owner are required");
      return;
    }

    setLoading(true);
    setError("");

    const payload = {
      name,
      description,
      status,
      owner_id: ownerId,
      start_date: startDate || null,
      end_date: endDate || null,
    };

    try {
      if (editingId) {
        await api.put(`/project/${editingId}`, payload);
      } else {
        await api.post("/project", payload);
      }

      resetForm();
      loadProjects();
    } catch (err) {
      console.error("PROJECT SAVE ERROR:", err);
      setError("Failed to save project");
    } finally {
      setLoading(false);
    }
  };

  /* ================= HELPERS ================= */

  const resetForm = () => {
    setName("");
    setDescription("");
    setStatus("Pending");
    setStartDate("");
    setEndDate("");
    setEditingId(null);
    if (employees.length > 0) {
      setOwnerId(employees[0].id);
    }
  };

  const handleEdit = (project) => {
    setName(project.name);
    setDescription(project.description || "");
    setStatus(project.status || "Pending");
    setOwnerId(project.owner_id);
    setStartDate(project.start_date ? project.start_date.slice(0, 10) : "");
    setEndDate(project.end_date ? project.end_date.slice(0, 10) : "");
    setEditingId(project.id);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this project?")) return;

    try {
      await api.delete(`/project/${id}`);
      loadProjects();
    } catch (err) {
      console.error("PROJECT DELETE ERROR:", err);
      setError("Failed to delete project");
    }
  };

  const getOwnerName = (ownerId) => {
    const emp = employees.find((e) => e.id === ownerId);
    return emp ? `${emp.first_name} ${emp.last_name}` : "Unknown";
  };

  /* ================= UI ================= */

  return (
    <div className="max-w-5xl mx-auto p-4">
      <h2 className="text-2xl font-bold mb-4 text-center">
        Project Management
      </h2>

      {error && <p className="text-red-500 mb-3">{error}</p>}

      {/* FORM */}
      <form
        onSubmit={handleSubmit}
        className="bg-white shadow rounded p-4 mb-6"
      >
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
          <input
            type="text"
            placeholder="Project Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="border px-2 py-1 rounded md:col-span-2"
            required
          />

          <input
            type="text"
            placeholder="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="border px-2 py-1 rounded md:col-span-2"
          />

          <select
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="border px-2 py-1 rounded"
          >
            <option value="Pending">Pending</option>
            <option value="In Progress">In Progress</option>
            <option value="Completed">Completed</option>
          </select>

          <select
            value={ownerId}
            onChange={(e) => setOwnerId(Number(e.target.value))}
            className="border px-2 py-1 rounded md:col-span-2"
            required
          >
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.first_name} {emp.last_name}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="border px-2 py-1 rounded"
          />

          <input
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="border px-2 py-1 rounded"
          />

          <button
            type="submit"
            disabled={loading}
            className={`px-4 py-1 rounded text-white ${
              editingId
                ? "bg-yellow-500 hover:bg-yellow-600"
                : "bg-blue-500 hover:bg-blue-600"
            }`}
          >
            {loading ? "Saving..." : editingId ? "Update" : "Add"}
          </button>
        </div>
      </form>

      {/* TABLE */}
      <table className="w-full border border-collapse">
        <thead className="bg-gray-200">
          <tr>
            <th className="border px-2 py-1">ID</th>
            <th className="border px-2 py-1">Name</th>
            <th className="border px-2 py-1">Status</th>
            <th className="border px-2 py-1">Start</th>
            <th className="border px-2 py-1">End</th>
            <th className="border px-2 py-1">Owner</th>
            <th className="border px-2 py-1">Actions</th>
          </tr>
        </thead>
        <tbody>
          {projects.map((p) => (
            <tr key={p.id}>
              <td className="border px-2 py-1 text-center">{p.id}</td>
              <td className="border px-2 py-1">{p.name}</td>
              <td className="border px-2 py-1">{p.status}</td>
              <td className="border px-2 py-1">{p.start_date?.slice(0,10) || "-"}</td>
              <td className="border px-2 py-1">{p.end_date?.slice(0,10) || "-"}</td>
              <td className="border px-2 py-1">{getOwnerName(p.owner_id)}</td>
              <td className="border px-2 py-1 text-center">
                <button
                  onClick={() => handleEdit(p)}
                  className="bg-yellow-400 text-white px-2 py-1 mr-2 rounded"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(p.id)}
                  className="bg-red-500 text-white px-2 py-1 rounded"
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
